import { Form, Input } from '~/shared/components/form';
import { UseFormReturn, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useChangePasswordValidationSchema } from '../lib/useValidationSchema';

import { Container, StyledCTAButton, StyledH4, StyledLoginTextblock } from '../styled';
import { Fields, LoginInputs } from '../models/fields';
import { TranslationKey } from '~/lib';

type ChangePasswordViewProps = {
    methods: UseFormReturn<LoginInputs>;
    setNewPassword: (payload: LoginInputs) => Promise<void>;
    authenticationError: string;
    renderAuthenticationError: (errorMessage: string) => JSX.Element;
    translate: (key: TranslationKey) => string;
};

export const ChangePasswordView = ({
    setNewPassword,
    authenticationError,
    renderAuthenticationError,
    translate,
}: ChangePasswordViewProps) => {
    const changePasswordSchema = useChangePasswordValidationSchema();
    const changePasswordMethods = useForm<LoginInputs>({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: yupResolver(changePasswordSchema),
    });

    const isValid = changePasswordMethods.formState.isValid;
    const isDirty = changePasswordMethods.formState.isDirty;

    const handleSubmit = async (data: LoginInputs) => {
        await setNewPassword(data);
    };

    return (
        <>
            <Form methods={changePasswordMethods} onSubmit={handleSubmit}>
                <Container>
                    <StyledH4>{translate('createAccount.changePassword')}</StyledH4>

                    <StyledLoginTextblock>
                        {translate('login.youCanChangeEasily')}
                    </StyledLoginTextblock>
                    <StyledLoginTextblock>
                        {translate('login.thePasswordShould')}
                    </StyledLoginTextblock>

                    <Input
                        data-testid="current-password"
                        type="password"
                        name={Fields.Password}
                        autoComplete="current-password"
                        id="password"
                        placeholder={translate('login.currentPassword')}
                        required
                    />

                    <Input
                        data-testid="new-password"
                        type="password"
                        name={Fields.NewPassword}
                        autoComplete="new-password"
                        id="newpassword"
                        placeholder={translate('login.newPassword')}
                        required
                    />

                    <Input
                        data-testid="confirm-password"
                        type="password"
                        name={Fields.RepeatPassword}
                        autoComplete="confirm-password"
                        id="repeatpassword"
                        placeholder={translate('createAccount.repeatNewPassword')}
                        required
                    />

                    <StyledCTAButton
                        data-testid="change-password"
                        type="submit"
                        variant="primary"
                        disabled={!isValid || !isDirty}
                    >
                        {translate('createAccount.changePasswordDK')}
                    </StyledCTAButton>

                    {authenticationError && renderAuthenticationError(authenticationError)}
                </Container>
            </Form>
        </>
    );
};
